import React from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import Banner from "../../components/Banner";
import LegalLinks from "../../components/LegalLinks";

type LegalSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

type LegalDocumentPageProps = {
  title: string;
  lastUpdated: string;
  sections: LegalSection[];
};

export default function LegalDocumentPage({ title, lastUpdated, sections }: LegalDocumentPageProps) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #eff3f8 0%, #f9fbfd 45%, #ffffff 100%)",
      }}
    >
      <Banner />
      <Container maxWidth="md" sx={{ pt: 11, pb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            backdropFilter: "blur(2px)",
            color: "#101828",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, mb: 0.8, letterSpacing: "-0.02em", color: "#101828" }}
          >
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#475467", mb: 3 }}>
            Last Updated: {lastUpdated}
          </Typography>

          {sections.map((section) => (
            <Box key={section.heading} sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1.1, color: "#101828" }}>
                {section.heading}
              </Typography>

              {(section.paragraphs ?? []).map((paragraph) => (
                <Typography key={paragraph} sx={{ mb: 1.1, lineHeight: 1.65, color: "#344054" }}>
                  {paragraph}
                </Typography>
              ))}

              {(section.bullets ?? []).length > 0 && (
                <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                  {section.bullets?.map((bullet) => (
                    <Typography key={bullet} component="li" sx={{ mb: 0.6, lineHeight: 1.6, color: "#344054" }}>
                      {bullet}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}

          <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid", borderColor: "divider", color: "#667085" }}>
            <LegalLinks centered />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export type { LegalSection };